import { IsInModalProvider } from 'hooks/isInModal';
import { theme } from 'lib/theme';
import * as React from 'react';
import { View, StyleSheet, Platform, PointerEvent } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useDispatch } from 'react-redux';
import { eventModalBackdropPress } from 'store/actions';

type Props = {
  index: number;
  maxIndex: number;
  windowWidth: number;
  windowHeight: number;
  keyboardHeight: number;
  size: 'fullscreen' | 'large' | 'small';
  children: React.ReactNode;
};

export const Modal: React.FC<Props> = ({
  index,
  maxIndex,
  children,
  windowWidth,
  windowHeight: rawWindowHeight,
  keyboardHeight,
  size,
}) => {
  const dispatch = useDispatch();
  const isKeyboardOpen = !!keyboardHeight;
  const { top: safeAreaTopRaw } = useSafeAreaInsets();

  const safeAreaTop = safeAreaTopRaw + (Platform.OS === 'web' ? 12 : 0);

  const safeAreaVertical = 0;
  const windowHeight = rawWindowHeight - safeAreaVertical;
  const screenIsSmall = Math.min(windowWidth, windowHeight) < 500;
  const isTop = index === maxIndex;
  const isSecond = maxIndex - index === 1;
  const isBottom = index === 0;

  let finalHeight = 0;
  let finalWidth = 0;
  let finalTop = 0;

  const finalWindowHeight = windowHeight - safeAreaTop; // - safeAreaTop - safeAreaBottom - keyboardHeight;

  const width = {
    small:
      Platform.OS === 'web'
        ? Math.min(windowWidth, 500)
        : Math.min(windowHeight, windowWidth, 500),
    large:
      Platform.OS === 'web'
        ? Math.min(windowWidth, 1000)
        : Math.min(windowHeight, windowWidth),
    fullscreen: windowWidth,
  }[size];

  const scaleChange = isTop || isSecond ? 0.96 : 0.92;

  if (screenIsSmall) {
    // Fullscreen for phones
    // finalHeight = Math.round(windowHeight - (keyboardHeight + finalMarginTop));
    const verticalMargin = 0;
    finalHeight = Math.round(finalWindowHeight);
    // finalHeight = Math.round(finalWindowHeight + verticalMargin);
    finalWidth = windowWidth;
    finalTop = Math.round(
      verticalMargin +
        // finalWindowHeight -
        //   finalHeight +
        safeAreaTop -
        (isTop ? 0 : isSecond ? 5 : 10),
    );
  } else {
    const verticalMargin = size === 'fullscreen' ? 25 : 40;
    const horizontalMargin = size === 'fullscreen' ? 25 : 40;

    const height = {
      small: Math.min(finalWindowHeight, 700),
      large: finalWindowHeight,
      fullscreen: finalWindowHeight,
    }[size];

    // Dependent on size of modal
    finalHeight = Math.round(
      height -
        (isTop && isKeyboardOpen ? verticalMargin * 2 : verticalMargin * 2),
    );
    finalWidth = width - horizontalMargin * 2;
    finalTop = Math.round(
      safeAreaTop +
        finalWindowHeight / 2 -
        finalHeight / 2 -
        // (isKeyboardOpen ? verticalMargin : 0) -
        (isTop ? 0 : isSecond ? 5 : 10),
    );
  }

  const styleTop = useSharedValue(windowHeight);
  const styleScale = useSharedValue(1);

  React.useEffect(() => {
    styleScale.value = withTiming(isTop ? 1 : scaleChange, {
      duration: 400,
      easing: Easing.out(Easing.exp),
    });
    styleTop.value = withTiming(finalTop, {
      duration: 400,
      easing: Easing.out(Easing.exp),
    });
  }, [styleTop, styleScale, isTop, finalTop, scaleChange]);

  const positionStyles = useAnimatedStyle(() => {
    return {
      transform: [{ translateY: styleTop.value }, { scale: styleScale.value }],
    };
  });

  const hasFocused = React.useRef(false);
  const pressableRef = React.useRef<View>(null);
  const previousFocus = React.useRef<typeof document.activeElement>(null);

  React.useEffect(() => {
    if (
      Platform.OS === 'web' &&
      pressableRef.current &&
      isTop &&
      !hasFocused.current
    ) {
      previousFocus.current = document.activeElement;
      pressableRef.current.focus();
      hasFocused.current = true;
    }
  }, [isTop, pressableRef]);

  React.useEffect(() => {
    return () => {
      if (Platform.OS === 'web' && previousFocus.current) {
        (previousFocus.current as HTMLElement).focus();
      }
    };
  }, []);

  const pointerDownRef = React.useRef<PointerEvent['target']>(null);

  return (
    <View
      ref={pressableRef}
      style={[
        StyleSheet.absoluteFill,
        styles.backdrop,
        isBottom ? { backgroundColor: '#00000050' } : undefined,
      ]}
      focusable={isTop}
      onPointerDown={(event: PointerEvent) => {
        /** @ts-expect-error target exists in react-native-web */
        pointerDownRef.current = event.nativeEvent.target;
      }}
      onPointerUp={(event: PointerEvent) => {
        if (pointerDownRef.current) {
          if (
            /** @ts-expect-error target exists in react-native-web */
            event.nativeEvent.target === pressableRef.current &&
            /** @ts-expect-error target exists in react-native-web */
            event.nativeEvent.target === pointerDownRef.current
          ) {
            dispatch(eventModalBackdropPress());
          }
          pointerDownRef.current = null;
        }
      }}
    >
      <Animated.View
        style={[
          styles.modal,
          {
            width: finalWidth,
            height: finalHeight,
            top: 0,
            transformOrigin: 'top',
          },
          isKeyboardOpen || screenIsSmall
            ? {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            : undefined,
          positionStyles,
        ]}
      >
        {!isTop && (
          <View
            style={[
              StyleSheet.absoluteFill,
              {
                backgroundColor: '#00000020',
                zIndex: 1,
              },
            ]}
          />
        )}
        <IsInModalProvider
          value={
            size === 'fullscreen' || screenIsSmall ? 'fullscreen' : 'windowed'
          }
        >
          {children}
        </IsInModalProvider>
      </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    alignItems: 'center',
  },
  modal: {
    backgroundColor: theme.colors.background,
    borderRadius: theme.modalBorderRadius,
    overflow: 'hidden',
    elevation: 4,
    shadowColor: 'black',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.1,
    shadowRadius: 40,
  },
});
